interface Point {
  x: number;
  y: number;
}

const EDGE_PADDING = 20;
const ALIGN_MAX_RETRIES = 60;
const PROFILE_SELECTOR = 'rt-candidate-profile-page';

export function getProfileElement(boundary: HTMLElement | undefined): HTMLElement | null {
  return boundary?.querySelector<HTMLElement>(PROFILE_SELECTOR) ?? null;
}

function getProfileRect(boundary: HTMLElement | undefined): DOMRect | undefined {
  return getProfileElement(boundary)?.getBoundingClientRect();
}

export function constrainToViewport(
  point: Point,
  dimensions: DOMRect,
  pickup: Point,
  nativeWindow: Window,
): Point {
  const left = point.x - pickup.x;
  const top = point.y - pickup.y;

  const maxLeft = Math.max(EDGE_PADDING, nativeWindow.innerWidth - EDGE_PADDING - dimensions.width);
  const clampedLeft = Math.min(Math.max(left, EDGE_PADDING), maxLeft);

  return { x: clampedLeft, y: top };
}

export function distanceFromProfileRight(
  boundary: HTMLElement | undefined,
  panelEl: HTMLElement,
): number | null {
  const rect = getProfileRect(boundary);
  if (!rect) {
    return null;
  }

  return rect.right - panelEl.getBoundingClientRect().right;
}

interface AlignToProfileOptions {
  boundary: HTMLElement | undefined;
  nativeWindow: Window;
  panelWidth: number;
  apply: (offsetPx: number) => void;
  distanceFromRight?: number;
}

export function alignToProfileRight(
  options: AlignToProfileOptions,
  retries = ALIGN_MAX_RETRIES,
): void {
  const { boundary, nativeWindow, panelWidth, apply, distanceFromRight = EDGE_PADDING } = options;
  const rect = getProfileRect(boundary);

  if (!rect || rect.width === 0) {
    if (retries > 0) {
      nativeWindow.requestAnimationFrame(() => alignToProfileRight(options, retries - 1));
    }

    return;
  }

  const viewportWidth = nativeWindow.innerWidth;
  const maxDistance = rect.right - panelWidth - EDGE_PADDING;
  const minDistance = rect.right - viewportWidth + EDGE_PADDING;
  const distance = Math.min(Math.max(distanceFromRight, minDistance), maxDistance);
  const rightOffset = Math.max(0, Math.round(viewportWidth - rect.right + distance));

  apply(rightOffset);
}
